import React from 'react';
import { Check, X, Eye, User, BookOpen, Heart, GraduationCap } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { getLocalizedText, formatPrice } from '../../utils/helpers';

export const PendingTab = ({ 
  data, 
  pendingNewCount,
  onOpenPreview, 
  onAction 
}) => {
  if (pendingNewCount === 0) {
    return (
      <div className="text-center py-12">
        <Check className="h-12 w-12 text-green-500 mx-auto mb-4" />
        <p className="text-gray-500">No hay nuevos elementos pendientes</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Pending Teachers */}
      {data.teachers.length > 0 && (
        <div>
          <h3 className="font-semibold mb-4 flex items-center gap-2">
            <User className="h-5 w-5 text-purple-500" />
            Nuevos Profesionales ({data.teachers.length})
          </h3>
          <div className="space-y-2">
            {data.teachers.map(teacher => (
              <div 
                key={teacher.id}
                onClick={() => onOpenPreview(teacher, 'teacher', `/api/admin/teachers/${teacher.id}/approve`)}
                className="flex items-center justify-between bg-gray-50 hover:bg-gray-100 rounded-lg p-4 cursor-pointer transition-colors"
              >
                <div className="flex items-center gap-3">
                  <img src={teacher.avatar || '/default-avatar.png'} alt="" className="w-10 h-10 rounded-full object-cover" />
                  <div>
                    <p className="font-medium">{teacher.user_name}</p>
                    <p className="text-sm text-gray-500">{teacher.user_email}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); onOpenPreview(teacher, 'teacher', `/api/admin/teachers/${teacher.id}/approve`); }}>
                    <Eye className="h-4 w-4" />
                  </Button>
                  <Button size="sm" className="bg-green-500" onClick={(e) => { e.stopPropagation(); onAction(`/api/admin/teachers/${teacher.id}/approve`); }}>
                    <Check className="h-4 w-4" />
                  </Button>
                  <Button size="sm" variant="destructive" onClick={(e) => { e.stopPropagation(); onAction(`/api/admin/teachers/${teacher.id}/reject`); }}>
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      
      {/* Pending Schools */}
      {data.schools.length > 0 && (
        <div>
          <h3 className="font-semibold mb-4 flex items-center gap-2">
            <BookOpen className="h-5 w-5 text-blue-500" />
            Nuevas Escuelas ({data.schools.length})
          </h3>
          <div className="space-y-2">
            {data.schools.map(school => (
              <div 
                key={school.id}
                onClick={() => onOpenPreview(school, 'school', `/api/admin/schools/${school.id}/approve`)}
                className="flex items-center justify-between bg-gray-50 hover:bg-gray-100 rounded-lg p-4 cursor-pointer transition-colors"
              >
                <div className="flex items-center gap-3">
                  <img src={school.images?.[0] || '/default-school.png'} alt="" className="w-10 h-10 rounded-lg object-cover" />
                  <div>
                    <p className="font-medium">{getLocalizedText(school.name, 'es')}</p>
                    <p className="text-sm text-gray-500">{school.user_email}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); onOpenPreview(school, 'school', `/api/admin/schools/${school.id}/approve`); }}>
                    <Eye className="h-4 w-4" />
                  </Button>
                  <Button size="sm" className="bg-green-500" onClick={(e) => { e.stopPropagation(); onAction(`/api/admin/schools/${school.id}/approve`); }}>
                    <Check className="h-4 w-4" />
                  </Button>
                  <Button size="sm" variant="destructive" onClick={(e) => { e.stopPropagation(); onAction(`/api/admin/schools/${school.id}/reject`); }}>
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      
      {/* Pending Services */}
      {data.services.length > 0 && (
        <div>
          <h3 className="font-semibold mb-4 flex items-center gap-2">
            <Heart className="h-5 w-5 text-rose-500" />
            Nuevos Servicios ({data.services.length})
          </h3>
          <div className="space-y-2">
            {data.services.map(service => (
              <div 
                key={service.id}
                onClick={() => onOpenPreview(service, 'service', `/api/admin/services/${service.id}/approve`)}
                className="flex items-center justify-between bg-gray-50 hover:bg-gray-100 rounded-lg p-4 cursor-pointer transition-colors"
              >
                <div className="flex items-center gap-3">
                  <img src={service.images?.[0] || '/default-service.png'} alt="" className="w-10 h-10 rounded-lg object-cover" />
                  <div>
                    <p className="font-medium">{getLocalizedText(service.title, 'es')}</p>
                    <p className="text-sm text-gray-500">{service.user_name} - {formatPrice(service.price, service.currency)}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); onOpenPreview(service, 'service', `/api/admin/services/${service.id}/approve`); }}>
                    <Eye className="h-4 w-4" />
                  </Button>
                  <Button size="sm" className="bg-green-500" onClick={(e) => { e.stopPropagation(); onAction(`/api/admin/services/${service.id}/approve`); }}>
                    <Check className="h-4 w-4" />
                  </Button>
                  <Button size="sm" variant="destructive" onClick={(e) => { e.stopPropagation(); onAction(`/api/admin/services/${service.id}/reject`); }}>
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      
      {/* Pending Courses */}
      {data.courses.length > 0 && (
        <div>
          <h3 className="font-semibold mb-4 flex items-center gap-2">
            <GraduationCap className="h-5 w-5 text-emerald-500" />
            Nuevos Cursos ({data.courses.length})
          </h3>
          <div className="space-y-2">
            {data.courses.map(course => (
              <div 
                key={course.id}
                onClick={() => onOpenPreview(course, 'course', `/api/admin/courses/${course.id}/approve`)}
                className="flex items-center justify-between bg-gray-50 hover:bg-gray-100 rounded-lg p-4 cursor-pointer transition-colors"
              >
                <div className="flex items-center gap-3">
                  <img src={course.images?.[0] || '/default-course.png'} alt="" className="w-10 h-10 rounded-lg object-cover" />
                  <div>
                    <p className="font-medium">{getLocalizedText(course.title, 'es')}</p>
                    <p className="text-sm text-gray-500">{course.school_name} - {formatPrice(course.price, course.currency)}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); onOpenPreview(course, 'course', `/api/admin/courses/${course.id}/approve`); }}>
                    <Eye className="h-4 w-4" />
                  </Button>
                  <Button size="sm" className="bg-green-500" onClick={(e) => { e.stopPropagation(); onAction(`/api/admin/courses/${course.id}/approve`); }}>
                    <Check className="h-4 w-4" />
                  </Button>
                  <Button size="sm" variant="destructive" onClick={(e) => { e.stopPropagation(); onAction(`/api/admin/courses/${course.id}/reject`); }}>
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      
      {/* Pending Avatars */}
      {data.avatars.length > 0 && (
        <div>
          <h3 className="font-semibold mb-4 flex items-center gap-2">
            <User className="h-5 w-5 text-indigo-500" />
            Nuevos Avatares ({data.avatars.length})
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {data.avatars.map(avatar => (
              <div key={avatar.id} className="bg-gray-50 rounded-lg p-4 text-center">
                <img src={avatar.pending_avatar || avatar.avatar_pending} alt="" className="w-20 h-20 rounded-full object-cover mx-auto mb-2" />
                <p className="font-medium text-sm">{avatar.name}</p>
                <p className="text-xs text-gray-500 truncate">{avatar.email}</p>
                <div className="flex gap-2 justify-center mt-2">
                  <Button size="sm" className="bg-green-500 hover:bg-green-600" onClick={() => onAction(`/api/admin/avatars/${avatar.id}/approve`)}>
                    <Check className="h-4 w-4" />
                  </Button>
                  <Button size="sm" variant="destructive" onClick={() => onAction(`/api/admin/avatars/${avatar.id}/reject`)}>
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
